import type { DrupalNode } from 'next-drupal';

export type EmailRecipient = DrupalNode;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getAttrString(attrs: unknown, key: string): string | undefined {
  if (!isObject(attrs)) return undefined;
  const v = attrs[key];
  return typeof v === 'string' ? v : undefined;
}

function getStringFromAny(obj: unknown, keys: string[]): string | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const rec = obj as Record<string, unknown>;

  // First try direct properties
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === 'string' && v.trim() !== '') return v;
  }

  // Then try attributes if available
  const attrs = rec.attributes as Record<string, unknown> | undefined;
  if (attrs && typeof attrs === 'object') {
    for (const k of keys) {
      const v = attrs[k];
      if (typeof v === 'string' && v.trim() !== '') return v;
    }
  }

  return undefined;
}

function getRelId(recipient: DrupalNode, relName: string): string | undefined {
  const rels = isObject(recipient?.relationships)
    ? (recipient.relationships as Record<string, unknown>)
    : undefined;
  const rel =
    rels && isObject(rels[relName]) ? (rels[relName] as Record<string, unknown>) : undefined;
  const data = rel && rel.data;
  if (isObject(data) && typeof data.id === 'string') return data.id;
  if (
    Array.isArray(data) &&
    data[0] &&
    isObject(data[0]) &&
    typeof (data[0] as Record<string, unknown>).id === 'string'
  ) {
    return String((data[0] as Record<string, unknown>).id);
  }
  return undefined;
}

// Helper to extract a comma-separated list of names from arrays
function getListString(obj: unknown, keys: string[]): string {
  if (!obj || typeof obj !== 'object') return '';
  const rec = obj as Record<string, unknown>;

  for (const key of keys) {
    const value = rec[key];

    // Try direct property
    if (Array.isArray(value)) {
      const names = value
        .map(item => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            const itemObj = item as Record<string, unknown>;
            return itemObj.name || itemObj.title || '';
          }
          return '';
        })
        .filter(name => typeof name === 'string' && name.trim() !== '');
      if (names.length > 0) return names.join(', ');
    } else if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }

  // Try attributes
  const attrs = rec.attributes as Record<string, unknown> | undefined;
  if (attrs && typeof attrs === 'object') {
    for (const key of keys) {
      const value = attrs[key];

      if (Array.isArray(value)) {
        const names = value
          .map(item => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object') {
              const itemObj = item as Record<string, unknown>;
              return itemObj.name || itemObj.title || '';
            }
            return '';
          })
          .filter(name => typeof name === 'string' && name.trim() !== '');
        if (names.length > 0) return names.join(', ');
      } else if (typeof value === 'string' && value.trim() !== '') {
        return value;
      }
    }
  }

  return '';
}

export function getRecipientEmails(recipient: EmailRecipient | Record<string, unknown>): string[] {
  // Support next-drupal node shape
  const attrs = (recipient as DrupalNode)?.attributes as Record<string, unknown> | undefined;
  const relEmail = getAttrString(attrs, 'field_email') || getAttrString(attrs, 'mail');
  // Support both alt field names used in project
  const relEmailAlt =
    getAttrString(attrs, 'field_email_alt') || getAttrString(attrs, 'field_email_alternatif');

  // Also support simplified shape from client (Confirm/Review pages)
  const simple = recipient as Record<string, unknown>;
  const simpleEmail = typeof simple?.email === 'string' ? (simple.email as string) : undefined;
  const simpleEmailAlt =
    typeof simple?.email_alt === 'string'
      ? (simple.email_alt as string)
      : typeof simple?.field_email_alternatif === 'string'
        ? (simple.field_email_alternatif as string)
        : undefined;

  // Support direct field access (for cases where fields are at root level)
  const directEmail =
    typeof simple?.field_email === 'string' ? (simple.field_email as string) : undefined;
  const directEmailAlt =
    typeof simple?.field_email_alternatif === 'string'
      ? (simple.field_email_alternatif as string)
      : undefined;

  const list = [
    relEmail,
    relEmailAlt,
    simpleEmail,
    simpleEmailAlt,
    directEmail,
    directEmailAlt,
  ].filter((v): v is string => typeof v === 'string' && v.trim() !== '');
  // Unique, lowercased for normalisation
  return Array.from(new Set(list.map(e => e.toLowerCase())));
}

function getRecipientDisplayName(recipient: EmailRecipient | Record<string, unknown>): string {
  const node = recipient as DrupalNode;
  const attrs = node?.attributes as Record<string, unknown> | undefined;
  const first =
    getAttrString(attrs, 'field_first_name') ||
    getStringFromAny(recipient, ['first_name', 'field_first_name']);
  const last =
    getAttrString(attrs, 'field_family_name') ||
    getAttrString(attrs, 'field_last_name') ||
    getStringFromAny(recipient, ['family_name', 'field_family_name', 'field_last_name']);
  const title = getAttrString(attrs, 'title') || getStringFromAny(recipient, ['name', 'title']);
  const composed = [first, last].filter(Boolean).join(' ').trim();
  return composed || title || '';
}

export function recipientToTokenVars(
  recipient: EmailRecipient | Record<string, unknown>
): Record<string, string> {
  const node = recipient as DrupalNode;
  const attrs = (node && (node.attributes as Record<string, unknown>)) || undefined;
  const id =
    (node && typeof node.id === 'string' ? node.id : getStringFromAny(recipient, ['id'])) || '';

  // First name: try direct field first, then attributes
  const firstName =
    getStringFromAny(recipient, ['first_name', 'field_first_name']) ||
    getAttrString(attrs, 'field_first_name') ||
    '';

  // Family name: try direct field first, then attributes
  const familyName =
    getStringFromAny(recipient, ['family_name', 'field_family_name', 'field_last_name']) ||
    getAttrString(attrs, 'field_last_name') ||
    getAttrString(attrs, 'field_family_name') ||
    '';

  // Email: try multiple sources including direct fields
  const email =
    getStringFromAny(recipient, ['email', 'field_email', 'mail']) ||
    getAttrString(attrs, 'field_email') ||
    getAttrString(attrs, 'mail') ||
    '';

  // Alt Email: try multiple sources
  const emailAlt =
    getStringFromAny(recipient, ['email_alt', 'field_email_alternatif', 'field_email_alt']) ||
    getAttrString(attrs, 'field_email_alternatif') ||
    getAttrString(attrs, 'field_email_alt') ||
    '';

  // Civility: try direct field first, then attributes
  const civility =
    getStringFromAny(recipient, ['civility', 'field_civility']) ||
    getAttrString(attrs, 'field_civility') ||
    '';

  // Company name: try multiple sources
  let companyName = '';

  // 1. First check direct fields (for simple objects or pre-populated data)
  companyName = getStringFromAny(recipient, ['company_name', 'field_company_name']) || '';

  // 2. Try attributes for free text or direct field
  if (!companyName) {
    companyName =
      getAttrString(attrs, 'field_company_free_text_') ||
      getAttrString(attrs, 'field_company_name') ||
      '';
  }

  // 3. Try relationship field_company_organisation (most common in Drupal)
  if (!companyName && node?.relationships) {
    const companyRel = (node.relationships as Record<string, unknown>)?.field_company_organisation;
    if (companyRel && typeof companyRel === 'object') {
      const relData = (companyRel as Record<string, unknown>)?.data;

      // Handle both single object and array of objects
      if (Array.isArray(relData) && relData.length > 0) {
        const first = relData[0] as Record<string, unknown>;
        companyName = String(first?.title || first?.name || '');
      } else if (relData && typeof relData === 'object') {
        companyName = String(
          (relData as Record<string, unknown>)?.title ||
            (relData as Record<string, unknown>)?.name ||
            ''
        );
      }
    }
  }

  // 4. Try direct field_company_organisation if it's already resolved (included data)
  if (!companyName) {
    const directCompany = (recipient as Record<string, unknown>)?.field_company_organisation;
    if (directCompany) {
      if (Array.isArray(directCompany) && directCompany.length > 0) {
        const first = directCompany[0] as Record<string, unknown>;
        companyName = String(first?.title || first?.name || '');
      } else if (typeof directCompany === 'object') {
        companyName = String(
          (directCompany as Record<string, unknown>)?.title ||
            (directCompany as Record<string, unknown>)?.name ||
            ''
        );
      }
    }
  }

  // Company ID
  const companyId =
    getStringFromAny(recipient, ['company_id', 'field_company_organisation']) ||
    getRelId(node, 'field_company_organisation') ||
    '';

  // Position: try direct field first, then attributes
  const position =
    getStringFromAny(recipient, ['position', 'field_position_title', 'field_position']) ||
    getAttrString(attrs, 'field_position_title') ||
    getAttrString(attrs, 'field_position') ||
    '';

  // Department: try direct field first, then attributes
  const department =
    getStringFromAny(recipient, ['department', 'field_department_service', 'department_service']) ||
    getAttrString(attrs, 'field_department_service') ||
    '';

  // Country: try multiple sources (can be direct field or taxonomy term)
  let country = '';

  // 1. First check direct fields (for simple objects or pre-populated data)
  country = getStringFromAny(recipient, ['country', 'field_country', 'field_work_country']) || '';

  // 2. Try attributes
  if (!country) {
    country =
      getAttrString(attrs, 'field_work_country') || getAttrString(attrs, 'field_country') || '';
  }

  // 3. Try relationship field_country_region (MAIN field for country taxonomy)
  if (!country && node?.relationships) {
    const countryRel = (node.relationships as Record<string, unknown>)?.field_country_region;
    if (countryRel && typeof countryRel === 'object') {
      const relData = (countryRel as Record<string, unknown>)?.data;

      // Handle both single object and array of objects
      if (Array.isArray(relData) && relData.length > 0) {
        const first = relData[0] as Record<string, unknown>;
        country = String(first?.name || first?.title || '');
      } else if (relData && typeof relData === 'object') {
        country = String(
          (relData as Record<string, unknown>)?.name ||
            (relData as Record<string, unknown>)?.title ||
            ''
        );
      }
    }
  }

  // 4. Try direct field_country_region if it's already resolved (included data)
  if (!country) {
    const directCountry = (recipient as Record<string, unknown>)?.field_country_region;
    if (directCountry) {
      if (Array.isArray(directCountry) && directCountry.length > 0) {
        const first = directCountry[0] as Record<string, unknown>;
        country = String(first?.name || first?.title || '');
      } else if (typeof directCountry === 'object') {
        country = String(
          (directCountry as Record<string, unknown>)?.name ||
            (directCountry as Record<string, unknown>)?.title ||
            ''
        );
      }
    }
  }

  // 5. Try relationship field_work_country (if it's a taxonomy term reference)
  if (!country && node?.relationships) {
    const countryRel = (node.relationships as Record<string, unknown>)?.field_work_country;
    if (countryRel && typeof countryRel === 'object') {
      const relData = (countryRel as Record<string, unknown>)?.data;

      // Handle both single object and array of objects
      if (Array.isArray(relData) && relData.length > 0) {
        const first = relData[0] as Record<string, unknown>;
        country = String(first?.name || first?.title || '');
      } else if (relData && typeof relData === 'object') {
        country = String(
          (relData as Record<string, unknown>)?.name ||
            (relData as Record<string, unknown>)?.title ||
            ''
        );
      }
    }
  }

  // 4. Try direct field_work_country if it's already resolved (included data)
  if (!country) {
    const directCountry = (recipient as Record<string, unknown>)?.field_work_country;
    if (directCountry) {
      if (Array.isArray(directCountry) && directCountry.length > 0) {
        const first = directCountry[0] as Record<string, unknown>;
        country = String(first?.name || first?.title || '');
      } else if (typeof directCountry === 'object') {
        country = String(
          (directCountry as Record<string, unknown>)?.name ||
            (directCountry as Record<string, unknown>)?.title ||
            ''
        );
      }
    }
  }

  // 5. Same for field_country as fallback
  if (!country && node?.relationships) {
    const countryRel = (node.relationships as Record<string, unknown>)?.field_country;
    if (countryRel && typeof countryRel === 'object') {
      const relData = (countryRel as Record<string, unknown>)?.data;

      if (Array.isArray(relData) && relData.length > 0) {
        const first = relData[0] as Record<string, unknown>;
        country = String(first?.name || first?.title || '');
      } else if (relData && typeof relData === 'object') {
        country = String(
          (relData as Record<string, unknown>)?.name ||
            (relData as Record<string, unknown>)?.title ||
            ''
        );
      }
    }
  }

  // Phone: try direct field first, then attributes
  const phone =
    getStringFromAny(recipient, ['phone', 'field_telephone', 'field_phone_number', 'telephone']) ||
    getAttrString(attrs, 'field_telephone') ||
    getAttrString(attrs, 'field_phone_number') ||
    '';

  // Mobile: try direct field first, then attributes
  const mobile =
    getStringFromAny(recipient, ['mobile', 'field_mobile_phone', 'field_mobile', 'mobile_phone']) ||
    getAttrString(attrs, 'field_mobile_phone') ||
    getAttrString(attrs, 'field_mobile') ||
    '';

  // Requested working groups: extract as comma-separated list
  const requestedGroups = getListString(recipient, [
    'requested_groups',
    'field_requested_groups',
    'field_extranet_working_group',
  ]);

  // Themes: extract as comma-separated list
  const themes = getListString(recipient, ['themes', 'field_theme']);

  return {
    id: id,
    uuid: id,
    name: getRecipientDisplayName(recipient),
    civility: civility,
    family_name: familyName,
    first_name: firstName,
    email: email,
    email_alt: emailAlt,
    company_name: companyName,
    company_id: companyId,
    position: position,
    department_service: department,
    country: country,
    phone: phone,
    mobile: mobile,
    requested_groups: requestedGroups,
    themes: themes,
  };
}
/*
    Available email template tokens:
 
    {{id}} - {{uuid}} - {{name}} - {{civility}} - {{family_name}} - {{first_name}} - {{email}} - {{email_alt}} - {{company_name}} - {{company_id}} - {{position}} - {{department_service}} - {{country}} - {{phone}} - {{mobile}} - {{requested_groups}} - {{themes}}

    */
